/**
 * Table data service for MetricData Dev Tool
 * Handles dynamic column generation and data formatting for TanStack Table
 *
 * @module MetricDataDevTool/services/tableDataService
 */

import { formatMetricValue, getMetricDisplayName } from '../utils/metricDefinitions';

/**
 * Get column alignment based on column type
 * @param {string} columnId - Column identifier
 * @returns {string} Alignment ('left' or 'right')
 */
export function getColumnAlignment(columnId) {
  const rightAlignColumns = ['numberValue', 'recordCount', 'formattedValue'];
  return rightAlignColumns.includes(columnId) ? 'right' : 'left';
}

/**
 * Generate table columns based on aggregation configuration
 * @param {Object} aggregationConfig - Aggregation configuration
 * @param {boolean} aggregationConfig.byDate - Aggregate by date
 * @param {boolean} aggregationConfig.byDayPart - Aggregate by day part
 * @param {boolean} aggregationConfig.byDiningOption - Aggregate by dining option
 * @returns {Array<Object>} Column definitions for TanStack Table
 */
export function generateColumns(aggregationConfig) {
  const columns = [];
  const isAggregating = aggregationConfig.byDate ||
                        aggregationConfig.byDayPart ||
                        aggregationConfig.byDiningOption;

  // Add dimension columns based on aggregation
  if (aggregationConfig.byDate) {
    columns.push({
      id: 'date',
      accessorKey: 'date',
      header: 'Date',
      cell: (info) => info.getValue() || 'N/A',
      meta: {
        align: getColumnAlignment('date')
      }
    });
  }

  // Always show metric name
  columns.push({
    id: 'metricName',
    accessorKey: 'metricName',
    header: 'Metric Name',
    cell: (info) => getMetricDisplayName(info.getValue()),
    meta: {
      align: getColumnAlignment('metricName')
    }
  });

  if (aggregationConfig.byDayPart) {
    columns.push({
      id: 'dayPart',
      accessorKey: 'dayPart',
      header: 'Day Part',
      cell: (info) => info.getValue() || 'All',
      meta: {
        align: getColumnAlignment('dayPart')
      }
    });
  }

  if (aggregationConfig.byDiningOption) {
    columns.push({
      id: 'diningOption',
      accessorKey: 'diningOption',
      header: 'Dining Option',
      cell: (info) => info.getValue() || 'All',
      meta: {
        align: getColumnAlignment('diningOption')
      }
    });
  }

  // Add value column with formatted display
  columns.push({
    id: 'numberValue',
    accessorKey: 'numberValue',
    header: 'Value',
    cell: (info) => {
      const row = info.row.original;
      return formatMetricValue(row.metricName, info.getValue());
    },
    meta: {
      align: getColumnAlignment('numberValue')
    }
  });

  // Add record count for aggregated data
  if (isAggregating) {
    columns.push({
      id: 'recordCount',
      accessorKey: 'recordCount',
      header: 'Record Count',
      cell: (info) => info.getValue()?.toLocaleString() || '0',
      meta: {
        align: getColumnAlignment('recordCount')
      }
    });
  }

  // For non-aggregated data, add additional columns
  if (!isAggregating) {
    if (!aggregationConfig.byDayPart) {
      columns.push({
        id: 'dayPart',
        accessorKey: 'dayPart',
        header: 'Day Part',
        cell: (info) => info.getValue() || 'N/A',
        meta: {
          align: getColumnAlignment('dayPart')
        }
      });
    }

    if (!aggregationConfig.byDiningOption) {
      columns.push({
        id: 'diningOption',
        accessorKey: 'diningOption',
        header: 'Dining Option',
        cell: (info) => info.getValue() || 'N/A',
        meta: {
          align: getColumnAlignment('diningOption')
        }
      });
    }

    if (!aggregationConfig.byDate) {
      columns.push({
        id: 'date',
        accessorKey: 'date',
        header: 'Date',
        cell: (info) => info.getValue() || 'N/A',
        meta: {
          align: getColumnAlignment('date')
        }
      });
    }

    columns.push({
      id: 'hour',
      accessorKey: 'hour',
      header: 'Hour',
      cell: (info) => info.getValue() || 'N/A',
      meta: {
        align: getColumnAlignment('hour')
      }
    });

    columns.push({
      id: 'metricId',
      accessorKey: 'metricId',
      header: 'Metric ID',
      cell: (info) => info.getValue() || 'N/A',
      meta: {
        align: getColumnAlignment('metricId')
      }
    });
  }

  return columns;
}

/**
 * Format table data with pre-computed formatted values
 * @param {Array<Object>} data - Raw table data
 * @returns {Array<Object>} Formatted table data
 */
export function formatTableData(data) {
  return data.map(row => ({
    ...row,
    formattedValue: row.metricName
      ? formatMetricValue(row.metricName, row.numberValue)
      : (row.numberValue || 0).toString()
  }));
}

/**
 * Get column header display name
 * @param {string} columnId - Column identifier
 * @returns {string} Display name
 */
export function getColumnHeader(columnId) {
  const headers = {
    metricName: 'Metric Name',
    numberValue: 'Value',
    date: 'Date',
    dayPart: 'Day Part',
    diningOption: 'Dining Option',
    recordCount: 'Record Count',
    hour: 'Hour',
    formattedValue: 'Value',
    metricId: 'Metric ID'
  };

  return headers[columnId] || columnId;
}
